import SearchBox from '@components/common/search-box'
import SearchProduct from '@components/common/search-product'
import { useSearchQuery } from '@framework/get-search-query'
import cn from 'classnames'
import React, { useEffect, useRef, useState } from 'react'
// import SearchResultLoader from '@components/ui/loaders/search-result-loader';
import { Box, Skeleton, Text, VStack } from '@chakra-ui/react'
import Scrollbar from '@components/ui/scrollbar'
import { useStore } from '@contexts/store'
import useFreezeBodyScroll from '@hooks/use-freeze-body-scroll'
import { useDebounce } from '@hooks/useDebounce'
import Link from 'next/link'
import { userWithoutToken } from '@helper/userWithoutToken'

type Props = {
  className?: string
  searchId?: string
  variant?: 'border' | 'fill'
}

const Search = React.forwardRef<HTMLDivElement, Props>(
  (
    {
      className = 'md:w-[730px] 2xl:w-[800px]',
      searchId = 'search',
      variant = 'border',
    },
    ref,
  ) => {
    const {
      displayMobileSearch,
      closeMobileSearch,
      displaySearch,
      closeSearch,
      storeData,
      analytics,
      user,
    } = useStore()
    const [searchText, setSearchText] = useState('')
    const [inputFocus, setInputFocus] = useState<boolean>(false)

    const debouncedSearchText = useDebounce(searchText, 500)

    const { results, isLoading } = useSearchQuery(
      storeData?._id,
      debouncedSearchText as string,
    )

    useEffect(() => {
      if (debouncedSearchText) {
        analytics.track('Product searched', {
          user: userWithoutToken(user),
          searchText: debouncedSearchText,
        })
      }
    }, [analytics, debouncedSearchText, user])

    const inputRef = useRef<HTMLInputElement>(null)

    useFreezeBodyScroll(
      inputFocus || displaySearch || displayMobileSearch,
      inputRef,
    )
    function handleSearch(e: React.SyntheticEvent) {
      e.preventDefault()
    }
    function handleAutoSearch(e: React.FormEvent<HTMLInputElement>) {
      setSearchText(e.currentTarget.value)
    }
    function clear() {
      setSearchText('')
      setInputFocus(false)
      closeMobileSearch()
      closeSearch()
    }
    function enableInputFocus() {
      setInputFocus(true)
    }

    return (
      <Box
        ref={ref}
        className={cn(
          'flex-grow transition-all duration-200 ease-in-out',
          className,
        )}
        maxWidth={{ base: 'full', lg: '400px', xl: '650px' }}
      >
        {/* <ModalOverlay /> */}
        <Box
          left={0}
          // className={cn('overlay cursor-pointer', {
          className={cn(
            'overlay cursor-pointer invisible w-full h-full opacity-0 flex top-0 transition-all duration-300 fixed ',
            {
              open: displayMobileSearch,
              'input-focus-overlay-open': inputFocus === true,
              'open-search-overlay': displaySearch,
            },
          )}
          onClick={() => clear()}
        />
        {/* End of overlay */}

        <div className="w-full flex flex-col justify-center flex-shrink-0 relative z-30">
          <div className="flex flex-col mx-auto w-full">
            <SearchBox
              searchId={searchId}
              name="search"
              value={searchText}
              onSubmit={handleSearch}
              onChange={handleAutoSearch}
              onClear={clear}
              onBlur={() => {
                if (searchText) return
                setInputFocus(false)
              }}
              onFocus={() => enableInputFocus()}
              variant={variant}
              ref={inputRef}
            />
          </div>
          {/* End of searchbox */}

          {searchText && (
            <Box
              bg={'gray.50'}
              className="w-full absolute top-[56px] start-0 py-2.5 rounded-md flex flex-col overflow-hidden shadow-dropDown z-30"
            >
              <Scrollbar className="os-host-flexbox">
                <div className="w-full h-[380px]">
                  {isLoading || !results ? (
                    <VStack spacing="3" w="full" px="4">
                      {Array.from({ length: 5 }).map((_, idx) => (
                        <Skeleton
                          rounded="md"
                          key={`search-result-loader-key-${idx}`}
                          height="48px"
                          w="full"
                        />
                      ))}
                    </VStack>
                  ) : results?.data.length === 0 ? (
                    <Text textAlign="center" fontWeight="600">
                      No results
                    </Text>
                  ) : (
                    <>
                      {results?.data
                        ?.slice(0, 4)
                        .map((item: any, index: number) => (
                          <Box
                            key={`search-result-key-${index}`}
                            px="3"
                            py="2"
                            onClick={clear}
                            className="py-2.5 pl-5 pe-10 scroll-snap-align-start transition-colors duration-200 hover:bg-skin-two"
                          >
                            <SearchProduct item={item} key={index} />
                          </Box>
                        ))}
                      <div className="py-2.5 ps-5 pe-10 scroll-snap-align-start transition-colors duration-200 hover:bg-skin-two">
                        <Link
                          onClick={() => clear()}
                          href={`/search?q=${searchText}`}
                          style={{ width: '100%' }}
                        >
                          <Text
                            textAlign="center"
                            fontWeight="500"
                            fontSize="20px"
                            textDecoration="underline"
                          >
                            View more...
                          </Text>
                        </Link>
                      </div>
                    </>
                  )}
                </div>
              </Scrollbar>
            </Box>
          )}
          {/* End of search result */}
        </div>
      </Box>
    )
  },
)

Search.displayName = 'Search'

export default Search
